/**
 * @fileoverview Popover to display and manage linked payments for an indirect cost.
 * @module features/indirect-costs/components/LinkedPaymentsPopover
 */

import { ReactElement, useState } from 'react'
import { format } from 'date-fns'
import { Trash2, Link as LinkIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Money } from '@/shared/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui'
import { useLinkedPayments } from '../hooks/useLinkedPayments'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { unlinkPaymentFromCost } from '../api/indirectCostsLinksService'
import type { IndirectCostEntryDTO } from '../schemas/indirectCostsSchema'

export interface LinkedPaymentsPopoverProps {
  cost: IndirectCostEntryDTO
  onUpdated?: () => void
  onPaymentUnlinked?: () => void
}

/**
 * Popover component that displays linked payments for an indirect cost.
 * Provides functionality to unlink payments.
 *
 * @component
 */
export default function LinkedPaymentsPopover({
  cost,
  onUpdated,
  onPaymentUnlinked,
}: LinkedPaymentsPopoverProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const [unlinkConfirmUuid, setUnlinkConfirmUuid] = useState<string | null>(null)

  const { data: linkedData, isLoading, error } = useLinkedPayments(cost.uuid)
  const linkedPayments = linkedData?.payments || []

  const unlinkMutation = useMutation({
    mutationFn: async (paymentUuid: string) => {
      await unlinkPaymentFromCost(cost.uuid, paymentUuid)
    },
    onSuccess: () => {
      toast.success(t('linkedPayments.unlink.success'))
      void queryClient.invalidateQueries({
        queryKey: ['indirect-costs', 'linked-payments', cost.uuid],
      })
      void queryClient.invalidateQueries({ queryKey: ['indirect-costs'] })
      onUpdated?.()
      onPaymentUnlinked?.()
      setUnlinkConfirmUuid(null)
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : t('linkedPayments.unlink.error')
      toast.error(message)
    },
  })

  const handleUnlinkPayment = (paymentUuid: string): void => {
    setUnlinkConfirmUuid(paymentUuid)
  }

  const confirmUnlink = (): void => {
    if (unlinkConfirmUuid) {
      unlinkMutation.mutate(unlinkConfirmUuid)
    }
  }

  if (isLoading) {
    return (
      <Popover
        onOpenChange={open => {
          if (!open) {
            setUnlinkConfirmUuid(null)
          }
        }}
      >
        <PopoverTrigger asChild>
          <button aria-label={t('aria.viewLinked')} className="glass-button rounded-md p-2">
            <LinkIcon className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[42rem] p-4 rounded-lg shadow-lg"
          align="end"
          sideOffset={8}
          side="bottom"
        >
          <div className="text-center text-sm text-muted-foreground">
            {t('linkedPayments.loading')}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  if (error) {
    let errorMessage = error instanceof Error ? error.message : t('linkedPayments.loadError')

    // Check for specific data validation error (undefined UUIDs in linked payments)
    if (
      typeof errorMessage === 'string' &&
      (errorMessage.includes('expected string, received undefined') ||
        errorMessage.includes('Invalid input')) &&
      errorMessage.includes('indirectCosts') &&
      errorMessage.includes('uuid')
    ) {
      errorMessage = t('linkedPayments.dataError')
    }

    return (
      <Popover
        onOpenChange={open => {
          if (!open) {
            setUnlinkConfirmUuid(null)
          }
        }}
      >
        <PopoverTrigger asChild>
          <button aria-label={t('aria.viewLinked')} className="glass-button rounded-md p-2">
            <LinkIcon className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[42rem] p-4 rounded-lg shadow-lg"
          align="end"
          sideOffset={8}
          side="bottom"
        >
          <div className="text-center text-sm text-destructive">{errorMessage}</div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <>
      <Popover
        onOpenChange={open => {
          if (!open) {
            setUnlinkConfirmUuid(null)
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            aria-label={t('aria.viewLinked')}
            className="glass-button rounded-md px-2 py-1 flex items-center gap-1"
          >
            <LinkIcon className="w-4 h-4 text-muted-foreground" />
            {linkedPayments.length > 0 && (
              <span className="text-xs font-semibold text-muted-foreground">
                {linkedPayments.length}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[42rem] p-0 rounded-lg shadow-lg"
          align="end"
          sideOffset={8}
          side="bottom"
        >
          <div className="p-4 border-b border-border">
            <div className="flex flex-col gap-1">
              <h3 className="font-semibold text-foreground">{t('linkedPayments.title')}</h3>
              <span className="text-xs font-medium text-muted-foreground">
                {cost.subcategoryDisplayName}
              </span>
            </div>
          </div>

          <div className="max-h-80 overflow-auto scrollbar-elegant">
            {linkedPayments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <LinkIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('linkedPayments.empty')}</p>
              </div>
            ) : (
              <div className="p-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        {t('linkedPayments.table.columns.number')}
                      </TableHead>
                      <TableHead className="w-24 pl-6 border-l border-border/20">
                        {t('linkedPayments.table.columns.date')}
                      </TableHead>
                      <TableHead className="pl-6 border-l border-border/20">
                        {t('linkedPayments.table.columns.counterpartyTitle', {
                          defaultValue: 'Counterparty / Title',
                        })}
                      </TableHead>
                      <TableHead className="w-32 text-right pr-6 border-r border-border/20">
                        {t('linkedPayments.table.columns.amount')}
                      </TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedPayments.map((payment, idx) => (
                      <TableRow key={payment.paymentUuid}>
                        <TableCell className="text-xs text-muted-foreground select-none">
                          {idx + 1}
                        </TableCell>
                        <TableCell className="pl-6 text-xs text-muted-foreground select-none border-l border-border/20">
                          {(() => {
                            const d = new Date(payment.txnDate)
                            const day = format(d, 'dd')
                            const monthShort = [
                              'jan',
                              'feb',
                              'mar',
                              'apr',
                              'may',
                              'jun',
                              'jul',
                              'aug',
                              'sep',
                              'oct',
                              'nov',
                              'dec',
                            ][d.getMonth()]
                            return `${day} ${tCommon(`monthsShort.${monthShort}`)}`
                          })()}
                        </TableCell>
                        <TableCell className="pl-6 border-l border-border/20">
                          <div className="flex flex-col gap-1">
                            <span
                              className="text-foreground font-medium truncate max-w-[18rem]"
                              title={payment.title}
                            >
                              {payment.title}
                            </span>
                            <span
                              className="text-xs text-muted-foreground truncate max-w-[20rem]"
                              title={payment.counterpartyName || '—'}
                            >
                              {payment.counterpartyName || '—'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-semibold text-right pr-6 border-r border-border/20">
                          <Money
                            value={payment.reportingNetAmount ?? 0}
                            currency={payment.reportingCurrency}
                            minFraction={2}
                            maxFraction={2}
                            useGrouping={false}
                          />
                        </TableCell>
                        <TableCell>
                          {unlinkConfirmUuid === payment.paymentUuid ? (
                            <button
                              type="button"
                              className="text-xs text-destructive hover:bg-destructive/10 rounded px-1"
                              onClick={confirmUnlink}
                              disabled={unlinkMutation.isPending}
                            >
                              {t('actions.confirm')}
                            </button>
                          ) : (
                            <button
                              type="button"
                              aria-label={t('aria.unlinkPayment')}
                              className="text-destructive hover:bg-destructive/10 rounded p-1"
                              onClick={() => handleUnlinkPayment(payment.paymentUuid)}
                              disabled={unlinkMutation.isPending}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {linkedData?.reportingTotal && linkedPayments.length > 0 && (
            <div className="p-4 border-t border-border bg-muted/30">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-foreground">
                  {t('linkedPayments.total')}
                </span>
                <Money
                  value={linkedData.reportingTotal}
                  currency={linkedData.reportingCurrency}
                  minFraction={2}
                  maxFraction={2}
                  useGrouping={false}
                  className="font-semibold"
                />
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  )
}
